import { Component, Input, OnChanges, ViewChild, TemplateRef, ViewContainerRef, ViewEncapsulation , CUSTOM_ELEMENTS_SCHEMA} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { State } from '@core/ngrx/reducers';
import { userSelectors } from '@app/core/ngrx/selectors';
import { faAngleRight, faChevronDown, faChevronRight, faChevronUp, faList } from '@fortawesome/pro-solid-svg-icons';
import { NavController } from '@ionic/angular';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { fromEvent, Subscription } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';
import { filter, take } from 'rxjs/operators';

import { QueryService } from '@app/core/graphql/query/query.service';
import { ToolService } from '@app/core/services/tool/tool.service';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatExpansionModule } from '@angular/material/expansion';
import { PipesComponentsModule } from '@app/shared/pipes/pipes.modules';

@Component({
    selector: 'app-user-applications-list',
    templateUrl: './user-applications-list.component.html',
    styleUrls: ['./user-applications-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        IonicModule,
        FontAwesomeModule,
        MatExpansionModule,
        PipesComponentsModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]})
export class UserApplicationsListComponent implements OnChanges {
    faAngleRight = faAngleRight;
    faList = faList;
    faChevronDown = faChevronDown;

    firstClick: any;

    @Input() applications: any;
    // application chain items
    @Input() chainApplications?: any[];

    censorContent$ = this.store.pipe(select(userSelectors.blockContent)); // visitor block content if 'unmanaged'

    overlayRef: OverlayRef | null; // right click menu check
    @ViewChild('navMenu') navMenu: TemplateRef<any>; // ng template for right click menu
    sub: Subscription;

    panelOpenState = false;
    content: any;

    constructor(
        private store: Store<State>,
        private navCtrl: NavController,
        public overlay: Overlay,
        public viewContainerRef: ViewContainerRef,
        private queryService: QueryService,
        public toolService: ToolService
    ) {
        this.toolService.content.subscribe((jsonContent: any) => {
            this.content = jsonContent;
        });
    }

    ngOnChanges(): void {
        if (this.chainApplications && this.chainApplications.length) {
            this.firstClick = this.chainApplications.map((a, index) => {
                return { [index]: false };
            });
        }
    }

    getFirstAndLastDatesOfChains(chains) {
        // identify the first value of the chain
        var lastItem = chains.application[chains.application.length - 1];
        return String(chains.application[0].startdate + ' ' + lastItem.enddate);
    }

    applicationRouting(application: any) {
        const query = this.createQuery(application);

        if (application.states === 'Pending-Booked' || application.states === 'Pre-Approved' || application.states === 'Completed') {
            this.navCtrl.navigateRoot(['/application/' + application.id]);
        } else if (application.states === 'Pending' && !application.program.preapprovalrequired) {
            this.navCtrl.navigateRoot(['/payment'], { queryParams: query });
        } else if (application.states === 'Pending' && application.program.preapprovalrequired) {
            this.navCtrl.navigateRoot(['/application/' + application.id]);
        } else if (application.requiresPayment) {
            this.navCtrl.navigateRoot(['/payment/'], { queryParams: query });
        } else if (
            (!application.requiresPayment && application.program.requiredpostdocumentlist.requireContract === true && application.signedcontract === null) ||
            (application.program.requiredpostdocumentlist.default && !application.signedcontract)
        ) {
            this.navCtrl.navigateRoot(['/application/' + application.id + '/clinical-agreement'], { queryParams: query });
        } else if (application.program.requiredpostdocumentlist.requireContract === false && application.signedcontract === null) {
            this.navCtrl.navigateRoot(['/application/' + application.id + '/clinical-agreement'], { queryParams: query });
        } else {
            this.navCtrl.navigateRoot(['/application/' + application.id]);
        }
    }

    applicationChainRouting(application: any) {
        this.navCtrl.navigateRoot(['/application-chain/' + application.id]);
    }

    openChainList(i: number) {
        this.firstClick[i] = !this.firstClick[i];
    }

    applicationChainSubRouting(id: string) {
        if (id) {
            this.queryService.applicationChainRouting(id).subscribe((res) => {
                const currentApplication = res.data.user.application;

                const query = this.createQuery(currentApplication);

                if (
                    currentApplication.states === 'Pending' ||
                    currentApplication.states === 'Pending-Booked' ||
                    currentApplication.states === 'Pre-Approved' ||
                    currentApplication.states === 'Completed'
                ) {
                    this.navCtrl.navigateRoot(['/application/' + currentApplication.id]);
                } else if (currentApplication.requiresPayment) {
                    this.navCtrl.navigateRoot(['/payment/'], {
                        queryParams: query,
                    });
                } else if (
                    (!currentApplication.requiresPayment &&
                        currentApplication.program.requiredpostdocumentlist.requireContract === true &&
                        currentApplication.signedcontract === null) ||
                    (currentApplication.program.requiredpostdocumentlist.default && !currentApplication.signedcontract)
                ) {
                    this.navCtrl.navigateRoot(['/application/' + currentApplication.id + '/clinical-agreement'], { queryParams: query });
                } else if (currentApplication.program.requiredpostdocumentlist.requireContract === false && currentApplication.signedcontract === null) {
                    this.navCtrl.navigateRoot(['/application/' + currentApplication.id + '/clinical-agreement'], { queryParams: query });
                } else {
                    this.navCtrl.navigateRoot(['/application/' + currentApplication.id]);
                }
            });
        }
    }

    toggle(expanded) {
        expanded = !expanded;
    }

    /**
     *
     * @param event mouse event, use coordinates to build template on
     * @param selectedTab string value of selected tab
     */
    open({ x, y }: MouseEvent, application: any) {
        let selectedTab: string;

        const params = this.createQuery(application);
        const query = Object.keys(params)
            .map((key) => key + '=' + params[key])
            .join('&');

        if (
            application.states === 'Pending' ||
            application.states === 'Pending-Booked' ||
            application.states === 'Pre-Approved' ||
            application.states === 'Completed'
        ) {
            selectedTab = `application/${application.id}`;
        } else if (application.requiresPayment) {
            selectedTab = `payment?${query}`;
        } else if (
            (!application.requiresPayment && application.program.requiredpostdocumentlist.requireContract === true && application.signedcontract === null) ||
            (application.program.requiredpostdocumentlist.default && !application.signedcontract)
        ) {
            selectedTab = `application/${application.id}/clinical-agreement?${query}`;
        } else if (application.program.requiredpostdocumentlist.requireContract === false && application.signedcontract === null) {
            selectedTab = `application/${application.id}/clinical-agreement?${query}`;
        } else {
            selectedTab = `application/${application.id}`;
        }

        // this try catch for event might not work later
        // need another way to add to params for event
        try {
            // eslint-disable-next-line import/no-deprecated
            event.preventDefault();
        } catch (err) {
            throw err;
        }

        this.close();
        // setup for context menu template
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo({ x, y })
            .withPositions([
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });
        // use ng template reference and attach tab string to it
        this.overlayRef.attach(
            new TemplatePortal(this.navMenu, this.viewContainerRef, {
                $implicit: selectedTab,
            })
        );

        this.sub = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event) => {
                    const clickTarget = event.target as HTMLElement;
                    return !!this.overlayRef && !this.overlayRef.overlayElement.contains(clickTarget);
                }),
                take(1)
            )
            .subscribe(() => this.close());
    }

    getPopoverMsg(application: any) {
        const isPrePayment = (application.states === 'Pending' && application?.program?.preapprovalrequired) || application.states === 'Pre-Approved';
        if (isPrePayment) {
            return this.content?.user?.components?.applicationsList?.popoverMsg['Pending-Pre-Approval'] || '';
        } else {
            return this.content?.user?.components?.applicationsList?.popoverMsg[application?.states] || '';
        }
    }

    /**
     * closes context menu
     */
    private close() {
        this.sub && this.sub.unsubscribe();
        if (this.overlayRef) {
            this.overlayRef.dispose();
            this.overlayRef = null;
        }
    }

    /**
     *
     * @param tab sting value for tab selected by right click context menu
     *
     */
    public openNewTab(tab: string) {
        const url = window.location.origin;
        window.open(`${url}/${tab}`, '_blank');
        this.close();
    }

    private createQuery(application: any): any {
        const query =
            application.states === 'Pending-Booked' || application.states === 'Pre-Approved'
                ? {}
                : application.states === 'Pending' && !application.program.preapprovalrequired
                ? { applicationid: application.id, status: 'Pending', type: 'booking' }
                : !application.requiresPayment && !application.signedcontract
                ? { applicationId: application.id, userId: application.userid }
                : !application.requiresPayment
                ? {}
                : application.madedeposit
                ? { applicationid: application.id, type: 'final-payment' }
                : { applicationid: application.id, type: 'first-payment' };

        return query;
    }
}
